import {
  IPortalInvoiceResponse,
  ISenderDetail,
  OrganisationFeatureFlags,
  StatusEnum,
  VerificationPossibleEmails,
  currencyFormatter,
  displayPhoneNumber,
  statusUIMapXero,
  toDecimal,
} from "shared";
import { useState } from "react";
import request from "axios";
import { toast } from "react-toastify";
import { When } from "react-if";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  BuildingLibraryIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClockIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

import { InvoicePaper } from "../../../common/Components/Invoice/InvoicePaper";
import { ChildFields } from "../../../common/Components/Invoice/ChildFields";
import { useInvoiceDetails } from "../../../common/hooks/useInvoiceDetails";
import { env } from "../../../common/lib/env";
import { mixpanelService } from "../../../common/lib/mixpanel";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Button } from "../../../common/Atoms/Button";
import { Notification } from "../../../common/Atoms/Notification";
import { StripeCheckout } from "../Stripe/StripeCheckout";
import { classNames } from "../../../common/lib/classNames";
import { DatePopover } from "../../../common/Components/DatePopover";
import { SmallSpinner } from "../../../common/Atoms/SmallSpinner";
import { AttachmentsModal } from "../../../common/Modals/AttachmentsModal";
import { Modal } from "../../../common/Atoms/Modal";
import { Verify } from "../Verify";
import { PaymentMethodsCard } from "../PaymentMethods/PaymentMethodsCard";
import {
  Banner,
  card,
  payButton,
  primaryPayButton,
  toolbarButton,
} from "../portalUi";

import { OtherAmountModal } from "./OtherAmountModal";
import { InvoicePaymentPlan } from "./InvoicePaymentPlan";
import { canCreatePaymentPlan, CreatePaymentPlan } from "./CreatePaymentPlan";
import { canPromiseToPay, PromiseToPay } from "./PromiseToPay";
import { canQueryInvoice, QueryInvoice } from "./QueryInvoice";

interface InvoicePageProps {
  invoiceData: IPortalInvoiceResponse;
  isFetching?: boolean;
}

export function InvoicePage({ invoiceData, isFetching }: InvoicePageProps) {
  const [downloading, setDownloading] = useState(false);
  const [checkingAttachments, setCheckingAttachments] = useState(false);
  const [hasAttachments, setHasAttachments] = useState(false);
  const [attachmentFileNames, setAttachmentFileNames] = useState<string[]>([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<`card` | `dd`>(`card`);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [otherAmountModal, setOtherAmountModal] = useState(false);
  const [otherAmount, setOtherAmount] = useState(invoiceData.invoice?.xero_amount_due);
  const [payNextInstalment, setPayNextInstalment] = useState<boolean | null>(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyList, setVerifyList] = useState<Array<Omit<VerificationPossibleEmails, `original`>>>([]);
  const [verifySenderDetail, setVerifySenderDetail] = useState<ISenderDetail>(invoiceData.senderDetail);
  const [pendingPayAction, setPendingPayAction] = useState<(() => void) | null>(null);
  const details = useInvoiceDetails(invoiceData.invoice);

  const shouldShowPayButton = (invoiceData.stripeEnabled && invoiceData.stripeConnectAccount && invoiceData.invoice?.xero_status === `AUTHORISED`);

  const pdfLink = `${env.basePortalApiUrl}invoices/code/${invoiceData.invoice?.hash}/download.pdf`;
  const attachmentsLink = `${env.basePortalApiUrl}invoices/code/${invoiceData.invoice?.hash}/attachments.zip`;

  async function checkForAttachments() {
    if (!invoiceData.invoice) return;
    if (hasAttachments) return; // Already checked and found attachments

    setCheckingAttachments(true);

    try {
      const response = await request({
        method: `GET`,
        url: `${env.basePortalApiUrl}invoices/code/${invoiceData.invoice?.hash}/check-attachments`,
        withCredentials: true,
      });

      setHasAttachments(response.data.hasAttachments);
      setAttachmentFileNames(response.data.fileNames);
    }
    catch (error) {
      console.error(`Error checking for attachments:`, error);
    }
    finally {
      setCheckingAttachments(false);
    }
  }

  async function onDownload() {
    if (!invoiceData.invoice) return;

    setDownloading(true);

    try {
      const filename = `${invoiceData.senderDetail.customerFacingCompanyName}_${invoiceData.invoice.xero_number}.pdf`;

      const result = await request({
        withCredentials: true,
        method: `GET`,
        responseType: `blob`,
        url: pdfLink,
        params: {
          filename,
          mode: `download`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));

      const link = document.createElement(`a`);
      link.href = url;
      link.target = `_blank`;

      link.setAttribute(`download`, filename);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download invoice`}`);
    }

    setDownloading(false);
  }

  async function onDownloadAttachments() {
    setDownloading(true);

    try {
      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: attachmentsLink,
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));

      const link = document.createElement(`a`);
      link.href = url;
      link.target = `_blank`;

      link.setAttribute(`download`, `attachments.zip`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not download attachments`}`);
    }

    setDownloading(false);
  }

  function onPrint() {
    window.open(pdfLink + `?mode=print`, `_blank`);
  }

  function onOpenExternal() {
    // TODO when other systems have online invoices, for now, only Xero
    mixpanelService.portalInvoiceXeroClick();

    window.open(invoiceData.externalOnlineLink, `_blank`, `noopener,noreferrer`);
  }

  async function showAttachmentsModal() {
    await checkForAttachments();
    setAttachmentsModalOpen(true);
  }

  function onPayOtherAmount() {
    setPaymentModalOpen(true);
    setOtherAmountModal(false);
  }

  function onPayInFull(method: `card` | `dd`) {
    setOtherAmount(invoiceData.invoice.xero_amount_due);
    setPaymentMethod(method);
    setPaymentModalOpen(true);
  }

  function onPayNextInstalment() {
    if (!invoiceData.invoice?.currentPaymentPlan) {
      toast.error(`No payment plan available`);

      return;
    }

    const nextInstalment = invoiceData.invoice.currentPaymentPlan.instalments
      // Should already be in order
      .find(i => {
        if ((i.amountCentsPaid || 0) < i.amountCents) {
          return true;
        }
      });

    if (!nextInstalment) {
      toast.error(`All installments are already paid`);

      return;
    }

    const balanceCents = nextInstalment.amountCents - (nextInstalment.amountCentsPaid || 0);

    if (balanceCents <= 0) {
      toast.error(`Next installment is already paid`);

      return;
    }

    setPayNextInstalment(true);
    setPaymentModalOpen(true);
  }

  if (!invoiceData.invoice || !details) {
    return <Notification
      type={ `error` }
    >
      { `Invoice not found` }
    </Notification>;
  }

  const allowedPartial = invoiceData.minPaymentPercent && invoiceData.minPaymentPercent < 100;
  // Stripe overrides the Pinch payment link when both are configured
  const showStripeCard = shouldShowPayButton;
  const directDebitLabel = invoiceData.stripeDirectDebitByCurrency?.[invoiceData.invoice.xero_currency?.toUpperCase()];
  const showStripeDD = shouldShowPayButton && !!directDebitLabel;
  const showPinch = !!invoiceData.paymentLink && !shouldShowPayButton;
  const hasFullRow = showStripeCard || showStripeDD || showPinch;
  const hasOtherRow = (showStripeCard || showStripeDD) && !!allowedPartial;

  const formatAmount = currencyFormatter(invoiceData.invoice.xero_currency);
  const amountDue = formatAmount(toDecimal(invoiceData.invoice.xero_amount_due));

  const unpaid = invoiceData.invoice.xero_status === StatusEnum.AUTHORISED;
  const paid = invoiceData.invoice.xero_status === StatusEnum.PAID;
  const timezone = invoiceData.invoice.organisationTimezone;
  const dueDate = details.dueDateString ? DateTime.fromISO(details.dueDateString, { zone: timezone }) : null;
  const overdueDays = unpaid && dueDate ?
    Math.floor(DateTime.now().setZone(timezone).startOf(`day`).diff(dueDate.startOf(`day`), `days`).days) :
    0;
  const issued = details.issueDateString ? DateTime.fromISO(details.issueDateString, { zone: timezone }).toFormat(`d LLL yyyy`) : null;
  const processingCount = invoiceData.processingStripePayments?.length ?? 0;
  const paymentInProgress = unpaid && processingCount > 0;

  // A payment that is still processing or waiting on the customer can still go through, so paying again may charge twice
  function confirmIfPaymentInProgress(action: () => void) {
    if (paymentInProgress) {
      setPendingPayAction(() => action);

      return;
    }

    action();
  }
  const showPaymentMethods = !!invoiceData.stripeEnabled && !!invoiceData.stripeConnectAccount && !!invoiceData.organisationFeatureFlags?.includes(OrganisationFeatureFlags.PORTAL_PAYMENT_METHODS);
  const hasOptions = canCreatePaymentPlan(invoiceData) || canPromiseToPay(invoiceData) || canQueryInvoice(invoiceData);
  const externalLabel = `${(shouldShowPayButton || invoiceData.paymentLink || invoiceData.invoice.xero_amount_due === 0) ? `View` : `View & Pay`} in ${invoiceData.language?.accountingSystemName}`;
  const partialMethods = [
    { value: `card`, title: `Card` },
    ...(showStripeDD ? [{ value: `dd`, title: directDebitLabel }] : []),
  ];

  const person = details.contact.primaryContactPerson;
  const addressLines: string[] = invoiceData.invoice.contact?.addressLines ?? [];
  const phoneLine = [
    displayPhoneNumber(person?.mobileCountryCode, person?.mobileNumber),
    displayPhoneNumber(person?.defaultCountryCode, person?.defaultNumber),
  ].filter(Boolean).join(` · `);

  const statusBadge = paid ? (
    <span className={ `whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-2 py-px text-xs font-medium text-green-700` }>{ `Paid` }</span>
  ) : overdueDays > 0 ? (
    <span className={ `whitespace-nowrap rounded-full border border-paidniceRed-200 bg-paidniceRed-50 px-2 py-px text-xs font-medium text-paidniceRed-700` }>{ `Overdue` }</span>
  ) : (
    <span className={ `whitespace-nowrap rounded-full border border-gray-200 bg-gray-100 px-2 py-px text-xs font-medium text-gray-700` }>
      { statusUIMapXero[details.status]?.portal || `Unknown Status` }
    </span>
  );

  return (
    <div className={ `mx-auto flex max-w-[93.75rem] flex-col gap-5 px-[clamp(1rem,3vw,2rem)] lg:pt-2` }>
      { /* Title and document actions */ }
      <div className={ `flex flex-wrap items-end justify-between gap-4` }>
        <div className={ `flex min-w-0 flex-col gap-2` }>
          <When condition={ !!invoiceData.contactLink }>
            <Link
              to={ invoiceData.contactLink }
              className={ `inline-flex items-center gap-1 self-start text-[0.8125rem] font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
            >
              <ChevronLeftIcon className={ `h-3.5 w-3.5` } />
              { `View other invoices` }
            </Link>
          </When>
          <div className={ `flex flex-wrap items-center gap-2.5` }>
            <h1 className={ `whitespace-nowrap text-2xl font-semibold leading-tight tracking-[-0.01em] text-gray-900` }>
              { `Invoice ${invoiceData.invoice.xero_number}` }
            </h1>
            { statusBadge }
            <When condition={ isFetching }>
              <SmallSpinner colored />
            </When>
          </div>
          <span className={ `text-sm text-gray-500` }>
            { `From ${invoiceData.senderDetail.customerFacingCompanyName}${issued ? ` · Issued ${issued}` : ``}` }
          </span>
        </div>
        <div className={ `flex flex-wrap gap-2` }>
          <button
            type={ `button` }
            className={ toolbarButton }
            onClick={ onDownload }
            disabled={ downloading }
          >
            <ArrowDownTrayIcon className={ `h-[0.9375rem] w-[0.9375rem]` } />
            { `Save as PDF` }
          </button>
          <button
            type={ `button` }
            className={ toolbarButton }
            onClick={ onPrint }
          >
            <PrinterIcon className={ `h-[0.9375rem] w-[0.9375rem]` } />
            { `Print` }
          </button>
          <button
            type={ `button` }
            className={ toolbarButton }
            onClick={ showAttachmentsModal }
            disabled={ downloading }
          >
            <PaperClipIcon className={ `h-[0.9375rem] w-[0.9375rem]` } />
            { `Attachments` }
          </button>
          <When condition={ !!invoiceData.externalOnlineLink }>
            { /* portal-external-link is a hook for per-org custom CSS, see orgOverrides */ }
            <button
              type={ `button` }
              className={ classNames(toolbarButton, `portal-external-link`) }
              onClick={ onOpenExternal }
            >
              { externalLabel }
              <ArrowTopRightOnSquareIcon className={ `h-3.5 w-3.5` } />
            </button>
          </When>
        </div>
      </div>

      <When condition={ paymentComplete }>
        <Banner
          tone={ `green` }
          icon={ CheckCircleIcon }
        >
          { /* Bank debits take days to clear, so only a card payment is known to have gone through */ }
          <span className={ `flex-1` }>{ paymentMethod === `card` ? `Payment successful!` : `Payment Submitted - please wait for updates` }</span>
          { /* Work around for race condition */ }
          <a
            href={ window.location.href }
            onClick={ () => window.location.reload() }
            className={ `text-[0.8125rem] text-green-700 underline` }
          >
            { `Reload` }
          </a>
        </Banner>
      </When>
      <When condition={ !!invoiceData.lateFeeFor }>
        { () => (
          <Banner
            tone={ `amber` }
            icon={ ExclamationTriangleIcon }
          >
            <span className={ `flex-1` }>
              { `This invoice was issued as a late fee for ` }
              <Link
                to={ `/i/${invoiceData.lateFeeFor.hash}` }
                className={ `text-amber-800 underline` }
              >
                { `${invoiceData.lateFeeFor.invoiceNumber} ↗` }
              </Link>
            </span>
          </Banner>
        ) }
      </When>
      <When condition={ paymentInProgress }>
        <Banner
          tone={ `amber` }
          icon={ ClockIcon }
        >
          <span className={ `flex-1` }>
            { `This invoice has ${processingCount} payment${processingCount === 1 ? `` : `s`} in progress.` }
          </span>
        </Banner>
      </When>

      { /* Pay rail on the right, moves above the invoice when there is no room */ }
      <div className={ `flex flex-row-reverse flex-wrap items-start gap-6` }>
        <div className={ `flex min-w-0 flex-[1_1_20rem] flex-col gap-4` }>
          <div className={ card }>
            <div className={ `flex flex-col gap-1 p-5` }>
              <span className={ `text-[0.8125rem] text-gray-500` }>{ `Amount due` }</span>
              <span className={ `text-[2rem] font-semibold leading-tight tracking-[-0.02em] tabular-nums text-gray-900` }>{ amountDue }</span>
              <When condition={ unpaid && !!dueDate }>
                { () => (
                  <span className={ `text-sm text-gray-600` }>
                    { `Due ${dueDate.toFormat(`ccc d LLL yyyy`)}` }
                    <When condition={ overdueDays > 0 }>
                      { ` · ` }
                      <span className={ `font-medium text-paidniceRed-700` }>
                        { `${overdueDays} day${overdueDays === 1 ? `` : `s`} overdue` }
                      </span>
                    </When>
                  </span>
                ) }
              </When>
              <When condition={ paid }>
                <span className={ `flex items-center gap-1.5 text-sm font-medium text-green-700` }>
                  <CheckCircleIcon className={ `h-4 w-4` } />
                  { `Paid in full` }
                </span>
              </When>
            </div>
            <When condition={ hasFullRow }>
              <div className={ `flex flex-col gap-2 px-5 pb-5` }>
                <When condition={ showStripeCard }>
                  <button
                    type={ `button` }
                    className={ primaryPayButton }
                    onClick={ () => confirmIfPaymentInProgress(() => onPayInFull(`card`)) }
                  >
                    <CreditCardIcon className={ `h-[1.125rem] w-[1.125rem]` } />
                    { `Pay by card` }
                  </button>
                </When>
                <When condition={ showStripeDD }>
                  <button
                    type={ `button` }
                    className={ `${payButton} border border-paidniceGreen-200 bg-white text-paidniceGreen-700 hover:bg-paidniceGreen-50` }
                    onClick={ () => confirmIfPaymentInProgress(() => onPayInFull(`dd`)) }
                  >
                    <BuildingLibraryIcon className={ `h-[1.125rem] w-[1.125rem]` } />
                    { `Pay by ${directDebitLabel}` }
                  </button>
                </When>
                <When condition={ showPinch }>
                  <a
                    href={ invoiceData.paymentLink }
                    target={ `_blank` }
                    rel={ `noreferrer noopener` }
                    className={ classNames(primaryPayButton, `hover:no-underline`) }
                  >
                    { `Pay now` }
                    <ArrowTopRightOnSquareIcon className={ `h-4 w-4` } />
                  </a>
                </When>
                <When condition={ hasOtherRow }>
                  <button
                    type={ `button` }
                    className={ `h-9 cursor-pointer whitespace-nowrap text-sm font-medium text-paidniceGreen-600 hover:underline` }
                    onClick={ () => confirmIfPaymentInProgress(() => {
                      setPaymentMethod(`card`);
                      setOtherAmountModal(true);
                    }) }
                  >
                    { `Pay a different amount` }
                  </button>
                </When>
              </div>
            </When>
            <When condition={ showPaymentMethods }>
              <PaymentMethodsCard
                compact
                resourceType={ `invoice` }
                code={ invoiceData.invoice.hash }
                stripeConnectAccount={ invoiceData.stripeConnectAccount }
                senderDetail={ invoiceData.senderDetail }
              />
            </When>
          </div>

          <When condition={ hasOptions }>
            { /* Status rows (promise made, query raised) sort above the actions */ }
            <div className={ classNames(card, `flex flex-col`) }>
              <div className={ `order-first px-5 pb-2.5 pt-3.5 text-[0.8125rem] font-semibold text-gray-900` }>{ `Other options` }</div>
              <CreatePaymentPlan invoiceData={ invoiceData } />
              <PromiseToPay
                invoiceData={ invoiceData }
                onRequireVerification={ () => {
                  setVerifyList(invoiceData.generatedRedactedEmailList ?? []);
                  setVerifySenderDetail(invoiceData.senderDetail);
                  setVerifyModalOpen(true);
                } }
              />
              <QueryInvoice
                invoiceData={ invoiceData }
                onRequireVerification={ () => {
                  setVerifyList(invoiceData.generatedRedactedEmailList ?? []);
                  setVerifySenderDetail(invoiceData.senderDetail);
                  setVerifyModalOpen(true);
                } }
              />
            </div>
          </When>
        </div>

        <div className={ `flex min-w-0 flex-[999_1_35rem] flex-col gap-4` }>
          <InvoicePaper
            mode={ `portal` }
            invoice={ invoiceData.invoice }
            details={ details }
            hiddenLineItemFields={ invoiceData.lineItemHiddenFields }
            sender={ invoiceData.senderDetail }
            meta={ (
              <>
                <dt className={ `text-gray-500` }>{ `Status` }</dt>
                <dd className={ `text-right` }>{ statusBadge }</dd>
              </>
            ) }
            top={ (
              <InvoicePaymentPlan
                invoiceData={ invoiceData }
                showPaymentButton={ shouldShowPayButton }
                onPayNextInstalment={ () => confirmIfPaymentInProgress(onPayNextInstalment) }
              />
            ) }
            billTo={ (
              <>
                <span className={ `text-[0.9375rem] font-semibold text-gray-900` }>{ details.contact.name }</span>
                <When condition={ !!details.contact.externalAccountNumber && details.contact.externalAccountNumber !== details.contact.name }>
                  <span className={ `text-[0.8125rem] text-gray-500` }>{ details.contact.externalAccountNumber }</span>
                </When>
                <When condition={ addressLines.length > 0 }>
                  <span className={ `whitespace-pre-line text-[0.8125rem] leading-relaxed text-gray-600` }>{ addressLines.join(`\n`) }</span>
                </When>
                <When condition={ !!phoneLine }>
                  <span className={ `text-[0.8125rem] text-gray-600` }>{ phoneLine }</span>
                </When>
                <When condition={ !!details.contact.tax_number }>
                  <span className={ `text-[0.8125rem] text-gray-500` }>{ `Tax number: ${details.contact.tax_number}` }</span>
                </When>
                <ChildFields childFields={ details.childFields } />
              </>
            ) }
            bottom={ invoiceData.footer ? (
              <div className={ `whitespace-pre-wrap border-t border-gray-100 px-[clamp(1rem,5cqi,2.5rem)] py-4 text-[0.8125rem] leading-relaxed text-gray-500` }>
                { invoiceData.footer }
              </div>
            ) : null }
          />

          <When condition={ invoiceData.hasLateFees?.length > 0 }>
            { () => (
              <div className={ card }>
                <div className={ `border-b border-gray-100 px-5 py-3.5 text-sm font-semibold text-gray-900` }>
                  { `Late fees applied to this invoice` }
                </div>
                { invoiceData.hasLateFees.map(lf => (
                  <div
                    key={ lf.hash }
                    className={ `flex flex-wrap justify-between gap-x-4 gap-y-2 px-5 py-3 text-sm tabular-nums` }
                  >
                    <Link
                      to={ `/i/${lf.hash}` }
                      className={ `font-medium text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
                    >
                      { `${lf.invoiceNumber} ↗` }
                    </Link>
                    <span className={ `flex-1 text-gray-500` }>
                      { `Issued ` }
                      <DatePopover
                        date={ lf.date }
                        organisationTimezone={ timezone }
                        labelFormat={ `d LLL yyyy` }
                        disablePopover
                      />
                    </span>
                    <span className={ `text-gray-900` }>{ lf.amount }</span>
                  </div>
                )) }
              </div>
            ) }
          </When>
        </div>
      </div>

      <OtherAmountModal
        isOpen={ otherAmountModal }
        onClose={ () => setOtherAmountModal(false) }
        otherAmount={ otherAmount }
        minAmountPercent={ invoiceData.minPaymentPercent }
        dueAmount={ invoiceData.invoice.xero_amount_due }
        setOtherAmount={ setOtherAmount }
        onPay={ onPayOtherAmount }
        methods={ partialMethods }
        method={ paymentMethod }
        setMethod={ method => setPaymentMethod(method as `card` | `dd`) }
      />

      <When condition={ paymentModalOpen }>
        { () => <StripeCheckout
          mode={ `invoice` }
          code={ invoiceData.invoice.hash }
          connectedAccountId={ invoiceData.stripeConnectAccount }
          otherAmount={ otherAmount }
          payNextInstalment={ payNextInstalment }
          onClose={ () => setPaymentModalOpen(false) }
          onPaid={ () => {
            setPaymentModalOpen(false);
            setPaymentComplete(true);
          } }
          paymentMethod={ paymentMethod }
        /> }
      </When>
      <AttachmentsModal
        isOpen={ attachmentsModalOpen }
        onClose={ () => setAttachmentsModalOpen(false) }
        attachments={ attachmentFileNames }
        isLoading={ checkingAttachments }
        onDownloadAttachment={ onDownloadAttachments }
        isDownloading={ downloading }
      />
      <Modal
        open={ verifyModalOpen }
        setOpen={ setVerifyModalOpen }
        title={ `` }
      >
        <Verify
          resourceType={ `invoice` }
          senderDetail={ verifySenderDetail }
          generatedRedactedEmailList={ verifyList }
          verificationMessage={ `You need to verify your session before adding or managing cards.` }
          inline
        />
      </Modal>
      <Modal
        open={ !!pendingPayAction }
        setOpen={ open => {
          if (!open) setPendingPayAction(null);
        } }
        title={ `Payment already in progress` }
      >
        <Paragraph className={ `mb-4` }>
          { `This invoice has ${processingCount} payment${processingCount === 1 ? `` : `s`} still processing or waiting for verification. If you pay again before ${processingCount === 1 ? `it clears` : `they clear`}, you may be charged twice.` }
        </Paragraph>
        <div className={ `flex justify-end gap-2` }>
          <Button
            color={ `gray` }
            onClick={ () => setPendingPayAction(null) }
          >
            { `Cancel` }
          </Button>
          <Button
            onClick={ () => {
              const action = pendingPayAction;

              setPendingPayAction(null);
              action?.();
            } }
          >
            { `Pay anyway` }
          </Button>
        </div>
      </Modal>
    </div>
  );
}
